import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Gift, Heart, MessageCircle, Package, PenLine } from "lucide-react";
import { imgUrl } from "@/lib/api";
import { BENEFITS, MANIFESTO_IMAGE } from "@/data/siteData";
import { Em, Reveal } from "./primitives";

const ICONS = [Gift, PenLine, Package, Heart, MessageCircle];

export default function Manifesto({ content }) {
  const c = content || {};
  const benefits = c.benefits?.length ? c.benefits : BENEFITS.map((b) => ({ title: b.title, description: b.description }));
  const image = c.image || MANIFESTO_IMAGE;
  const kicker = c.kicker || "03 — Our Promise";

  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [-40, 40]);

  return (
    <section id="about" ref={ref} className="py-24 md:py-32">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 md:px-12 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <Reveal>
              <p className="mb-4 text-xs font-semibold uppercase tracking-[0.3em] text-rose md:text-sm">
                {kicker}
              </p>
              <h2 className="font-serif text-4xl font-medium tracking-tight text-ink md:text-6xl">
                <Em text={c.title || "Why *Gifting Loves*?"} />
              </h2>
              <p className="mt-6 max-w-md text-sm leading-relaxed text-[#5C5C5C] md:text-base">
                {c.text || "We believe a gift is never just a thing. It is a feeling, folded carefully into paper and ribbon."}
              </p>
            </Reveal>
            <Reveal delay={0.15} className="mt-10">
              <motion.div
                style={{ y: imgY }}
                className="overflow-hidden rounded-3xl border border-line"
              >
                <img
                  src={imgUrl(image)}
                  alt="Gift wrapping flatlay with kraft paper, ribbon and scissors"
                  loading="lazy"
                  className="aspect-[4/3] w-full object-cover"
                />
              </motion.div>
            </Reveal>
          </div>
        </div>

        <div className="lg:col-span-7">
          {benefits.map((b, i) => {
            const Icon = ICONS[i % ICONS.length];
            return (
              <Reveal key={b.title} delay={0.05 * i}>
                <div
                  data-testid={`benefit-${i + 1}`}
                  className="group flex gap-6 border-t border-line py-10 md:gap-10 md:py-12"
                >
                  <span className="font-serif text-4xl font-light italic text-rose/70 md:text-6xl">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <div className="mb-3 flex items-center gap-3">
                      <Icon size={20} className="text-rose" />
                      <h3 className="font-serif text-2xl font-medium text-ink transition-colors duration-300 group-hover:text-rose md:text-3xl">
                        {b.title}
                      </h3>
                    </div>
                    <p className="max-w-lg text-sm leading-relaxed text-[#5C5C5C] md:text-base">
                      {b.description}
                    </p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
